import React, { useCallback, useState } from "react";
import { Form, Input, Select } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { useForm } from "antd/lib/form/Form";
import PrimaryButton from "../../ui/buttons/PrimaryButton";
import { apiCall, apiUrls } from "../../store/api";
import "./skillz-admin.scss";

type Environment = "stage" | "prod";

type SkillzAdminDashboardProps = {
  token: string | null;
  onLogout: () => void;
};

const SkillzAdminDashboard = ({
  token,
  onLogout,
}: SkillzAdminDashboardProps) => {
  const [form] = useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<Environment>("prod");

  const onSubmit = useCallback(
    async (formData: { userId: string }) => {
      setIsLoading(true);
      setError(null);
      setResponseMessage(null);
      try {
        if (!token) {
          throw new Error("Missing token");
        }
        const response = await apiCall({
          method: "post",
          url: apiUrls.skillzAdminResendUserMessages,
          params: { userID: formData.userId, token, env: environment },
        });
        setResponseMessage(JSON.stringify(response.data, null, 2));
      } catch (err) {
        setError("Failed to reset messages for this user ID");
      } finally {
        setIsLoading(false);
      }
    },
    [token, environment],
  );

  const onFillMissingSolutions = useCallback(async () => {
    const userId = form.getFieldValue("userId");
    if (!userId) {
      form.validateFields(["userId"]);
      return;
    }
    setIsLoading(true);
    setError(null);
    setResponseMessage(null);
    try {
      if (!token) {
        throw new Error("Missing token");
      }
      const response = await apiCall({
        method: "post",
        url: apiUrls.skillzAdminFillMissedSolutions,
        params: { userID: userId, token, env: environment },
      });
      setResponseMessage(JSON.stringify(response.data, null, 2));
    } catch (err) {
      setError("Failed to fill missing solutions for this user ID");
    } finally {
      setIsLoading(false);
    }
  }, [form, token, environment]);

  return (
    <div className="skillz-admin">
      <div className="skillz-admin__panel">
        <div className="skillz-admin__header">
          <h2>Skillz Admin Dashboard</h2>
          <PrimaryButton type="default" onClick={onLogout}>
            Logout
          </PrimaryButton>
        </div>
        <div className="skillz-admin__env-toggle">
          <span>Environment:</span>
          <Select
            value={environment}
            onChange={setEnvironment}
            style={{ width: 120 }}
            options={[
              { value: "prod", label: "Prod" },
              { value: "stage", label: "Stage" },
            ]}
          />
        </div>
        <Form form={form} onFinish={onSubmit} className="skillz-admin__form">
          <Form.Item
            name="userId"
            rules={[{ required: true, message: "User ID is required" }]}
          >
            <Input type="text" placeholder="User ID" />
          </Form.Item>
          <Form.Item>
            <PrimaryButton type="primary" htmlType="submit">
              Reset Messages
            </PrimaryButton>
          </Form.Item>
        </Form>
        {isLoading && (
          <div className="skillz-admin__loading-inline">
            <LoadingOutlined />
          </div>
        )}
        {error && <div className="skillz-admin__error">{error}</div>}
        {responseMessage && (
          <pre className="skillz-admin__response">{responseMessage}</pre>
        )}
      </div>
    </div>
  );
};

export default SkillzAdminDashboard;
